import { useState, useEffect } from "react";
import { Box, Paper, Typography, alpha } from "@mui/material";

import styles from "./ElementInspector.module.scss";

interface ElementInfo {
  tagName: string;
  id: string;
  classList: string[];
  componentId: string;
}

interface ElementInspectorProps {
  onSelectElement: (element: HTMLElement) => void;
  isActive: boolean;
  setIsActive: (isActive: boolean) => void;
}

function ElementInspector({ onSelectElement, isActive, setIsActive }: ElementInspectorProps): JSX.Element {
  const [hoveredElement, setHoveredElement] = useState<HTMLElement | null>(null);
  const [elementInfo, setElementInfo] = useState<ElementInfo | null>(null);

  /**
   * enable keyboard shortcuts to the inspector
   */
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.shiftKey && event.key === "I") {
        setIsActive(true);
      }

      // Escape key to deactivate
      if (event.key === "Escape") {
        setIsActive(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * turn on the inspector
   */
  useEffect(() => {
    if (!isActive) {
      return;
    }

    document.querySelectorAll("*").forEach((el) => {
      if (el instanceof HTMLAnchorElement) {
        el.setAttribute("original-href", el.href);
        el.removeAttribute("href");
        el.style.pointerEvents = "none";
      }
      if (el instanceof HTMLElement && typeof el.onclick === "function") {
        el.onclick = null;
      }
      el.removeEventListener?.("click", () => {});
    });

    const handleMouseOrClick = (event: MouseEvent): void => {
      event.preventDefault();
      event.stopPropagation();

      if (!event?.target || !(event.target instanceof HTMLElement)) {
        return;
      }

      const mouseX = event.clientX;
      const mouseY = event.clientY;

      //find all elements with the component-id attribute
      const editableComponents = (event.target as HTMLElement).querySelectorAll<HTMLElement>("[component-id]");
      let editableComponent: HTMLElement | null = null;
      for (const editableOption of Array.from(editableComponents ?? [])) {
        const componentId = editableOption.getAttribute("component-id");
        if (componentId) {
          editableComponent = editableOption;

          //calculate the position of the element to make sure the pointer is over the element
          const componentX = editableOption.getBoundingClientRect().x;
          const componentY = editableOption.getBoundingClientRect().y;
          const componentW = editableOption.getBoundingClientRect().width;
          const componentH = editableOption.getBoundingClientRect().height;

          //make sure the mouse is over the element
          if (mouseX >= componentX && mouseX <= componentX + componentW && mouseY >= componentY && mouseY <= componentY + componentH) {
            editableComponent = editableOption;
            break;
          }
        }
      }

      if (!editableComponent) {
        return;
      }

      setHoveredElement(editableComponent as HTMLElement);
      setElementInfo({
        tagName: editableComponent.tagName.toLowerCase(),
        id: editableComponent.id,
        classList: Array.from(editableComponent.classList),
        componentId: editableComponent.getAttribute("component-id") || "",
      });
      onSelectElement(editableComponent as HTMLElement);
    };

    document.addEventListener("mouseover", handleMouseOrClick, { passive: false });
    document.addEventListener("click", handleMouseOrClick, { passive: false });
    return () => {
      document.removeEventListener("mouseover", handleMouseOrClick);
      document.removeEventListener("click", handleMouseOrClick);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  if (!hoveredElement || !isActive || !elementInfo) {
    return <></>;
  }

  const rect = hoveredElement.getBoundingClientRect();
  return (
    <Box className={styles.overlay}>
      <Box
        sx={{
          position: "fixed",
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height,
          pointerEvents: "none",
          border: "2px solid",
          borderColor: "primary.main",
          backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
          zIndex: 9999,
        }}
      />
      <Paper
        elevation={3}
        className={styles.infoPanel}
        sx={{
          position: "fixed",
          top: rect.bottom + 5,
          left: rect.left,
          zIndex: 10000,
          pointerEvents: "none",
          backgroundColor: "background.paper",
          p: 1,
        }}
      >
        <Typography
          variant="body2"
          fontFamily="monospace"
          fontWeight="bold"
        >
          {elementInfo.tagName}
        </Typography>
        {elementInfo.id && (
          <Typography
            variant="body2"
            fontFamily="monospace"
            fontWeight="bold"
          >
            #{elementInfo.id}
          </Typography>
        )}
        {elementInfo.componentId && (
          <Typography
            variant="body2"
            fontFamily="monospace"
            fontWeight="bold"
          >
            {elementInfo.componentId}
          </Typography>
        )}
        {/*
          {elementInfo.classList.length > 0 && (
            <Typography
              variant="caption"
              color="primary"
              fontFamily="monospace"
              className={styles.classes}
            >
              {elementInfo.classList.map((c) => `.${c}`).join(" ")}
            </Typography>
          )}
          */}
      </Paper>
    </Box>
  );
}

export default ElementInspector;
